import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Truck, Package, MapPin, LogOut, Menu, X, User, CheckCircle, Search, ShieldCheck, Camera, AlertTriangle, Bell, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { API, toast } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import TrackingSearch from '@/components/shared/TrackingSearch';
import NotificationBell from '@/components/shared/NotificationBell';
import {
  UI_ACTION_LABELS,
  UI_FEEDBACK_MESSAGES,
  UI_NAV_LABELS,
  UI_ROLE_LABELS,
} from '@/constants/uiLabels';
import { clearAuthSession } from '@/utils/authSession';
import logoSidebar from '@/assets/logo-cn-nr-ps.png';

const POD_PHOTO_MAX_SIZE_BYTES = 8 * 1024 * 1024;
const POD_PHOTO_ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

const LivreurDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [weightVerified, setWeightVerified] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('missions'); // nouveau state pour les onglets
  const [showPodDialog, setShowPodDialog] = useState(false);
  const [podOtpCode, setPodOtpCode] = useState('');
  const [podComment, setPodComment] = useState('');
  const [podProofType, setPodProofType] = useState('delivered');
  const [podPhotoUrl, setPodPhotoUrl] = useState('');
  const [podPhotoFileId, setPodPhotoFileId] = useState('');
  const [podPhotoFileName, setPodPhotoFileName] = useState('');
  const [podBusy, setPodBusy] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [focusedDeliveryId, setFocusedDeliveryId] = useState('');
  const [priorityOnly, setPriorityOnly] = useState(false);

  useEffect(() => {
    loadUserData();
    loadDeliveries();
    
    // Update location every 30 seconds
    const locationInterval = setInterval(updateLocation, 30000);
    updateLocation();
    
    return () => clearInterval(locationInterval);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const requestedTab = params.get('tab');
    const requestedDeliveryId = String(params.get('deliveryId') || '').trim();

    if (requestedTab === 'missions' || requestedTab === 'tracking') {
      setActiveTab(requestedTab);
    }
    setFocusedDeliveryId(requestedDeliveryId);
  }, [location.search]);

  useEffect(() => {
    if (!focusedDeliveryId || activeTab !== 'missions') {
      return undefined;
    }

    const matchingCard = document.querySelector(`[data-delivery-card-id="${focusedDeliveryId}"]`);
    if (matchingCard && typeof matchingCard.scrollIntoView === 'function') {
      matchingCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    const timeout = setTimeout(() => {
      setFocusedDeliveryId((currentValue) => (currentValue === focusedDeliveryId ? '' : currentValue));
    }, 4000);

    return () => clearTimeout(timeout);
  }, [focusedDeliveryId, activeTab, deliveries]);

  const loadUserData = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (_error) {
      console.warn('Impossible de charger le profil livreur.');
    }
  };

  const loadDeliveries = async () => {
    try {
      const response = await axios.get(`${API}/deliveries`);
      const terminalStatuses = new Set(['delivered', 'delivered_with_proof', 'cancelled', 'delivery_failed']);
      setDeliveries(response.data.filter((delivery) => !terminalStatuses.has(delivery.status)));
    } catch (_error) {
      console.warn('Impossible de charger les livraisons livreur.');
    }
  };

  const updateLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            await axios.patch(`${API}/livreur/location`, null, {
              params: {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              }
            });
          } catch (error) {
            console.error('Location update failed:', error);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
        }
      );
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus || !selectedDelivery?.id || statusUpdating) return;

    setStatusUpdating(true);
    try {
      await axios.patch(
        `${API}/deliveries/${selectedDelivery.id}/status`,
        null,
        {
          params: {
            status: newStatus,
            weight_verified: weightVerified || undefined
          }
        }
      );
      
      toast.success(UI_FEEDBACK_MESSAGES.statusUpdatedSuccess);
      setShowStatusDialog(false);
      setSelectedDelivery(null);
      setNewStatus('');
      setWeightVerified('');
      await loadDeliveries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de mettre à jour le statut.');
    } finally {
      setStatusUpdating(false);
    }
  };

  const isPodEligibleDelivery = (delivery) => {
    const allowedStatuses = ['out_for_delivery', 'client_absent', 'delivery_rescheduled'];
    return allowedStatuses.includes(delivery.status);
  };

  const syncDeliveryState = (deliveryId, updates = {}) => {
    if (!deliveryId || !updates || Object.keys(updates).length === 0) {
      return;
    }

    setDeliveries((currentDeliveries) => currentDeliveries.map((delivery) => (
      delivery.id === deliveryId ? { ...delivery, ...updates } : delivery
    )));
    setSelectedDelivery((currentDelivery) => (
      currentDelivery?.id === deliveryId ? { ...currentDelivery, ...updates } : currentDelivery
    ));
  };

  const getDeliveryConfirmation = (delivery) => {
    const confirmation = delivery?.delivery_confirmation || {};
    const otp = confirmation.otp || {};
    return {
      configured_mode: confirmation.configured_mode || 'photo_only',
      effective_mode: confirmation.effective_mode || 'photo_only',
      otp_required: Boolean(confirmation.otp_required),
      photo_required: Boolean(confirmation.photo_required),
      otp_verified: Boolean(confirmation.otp_verified),
      otp_verified_at: confirmation.otp_verified_at || null,
      fallback_reason: confirmation.fallback_reason || '',
      fallback_to_photo_available: Boolean(confirmation.fallback_to_photo_available),
      tracking_email_confirmed: Boolean(confirmation.tracking_email_confirmed),
      tracking_email_masked: confirmation.tracking_email_masked || '',
      can_generate_otp: Boolean(confirmation.can_generate_otp),
      completed_via: confirmation.completed_via || '',
      otp: {
        status: otp.status || 'not_available',
        verified: Boolean(otp.verified),
        verified_at: otp.verified_at || null,
        expires_at: otp.expires_at || null,
        attempts_count: Number(otp.attempts_count || 0),
        max_attempts: Number(otp.max_attempts || 0),
        masked_code: otp.masked_code || '',
      },
    };
  };

  const getDeliveryConfirmationBadge = (delivery) => {
    const confirmation = getDeliveryConfirmation(delivery);
    const otpStatus = String(confirmation.otp.status || '').trim().toLowerCase();

    if (confirmation.otp_verified && confirmation.photo_required) {
      return {
        key: 'delivery-confirmation-otp-photo',
        label: 'OTP validé, photo requise',
        tone: 'trust',
        description: 'Le code client est validé. Envoyez maintenant la photo de remise.',
      };
    }
    if (confirmation.otp_verified) {
      return {
        key: 'delivery-confirmation-otp-verified',
        label: 'OTP validé',
        tone: 'trust',
        description: 'La remise peut être finalisée avec le code client déjà validé.',
      };
    }
    if (confirmation.otp_required && otpStatus === 'pending') {
      return {
        key: 'delivery-confirmation-otp-pending',
        label: 'OTP requis',
        tone: 'protection',
        description: 'Un code client est actif pour cette livraison.',
      };
    }
    if (confirmation.otp_required && otpStatus === 'expired') {
      return {
        key: 'delivery-confirmation-otp-expired',
        label: 'OTP expiré',
        tone: 'critical',
        description: 'Générez un nouveau code ou utilisez la photo si le fallback est autorisé.',
      };
    }
    if (confirmation.otp_required && otpStatus === 'locked') {
      return {
        key: 'delivery-confirmation-otp-locked',
        label: 'OTP bloqué',
        tone: 'critical',
        description: 'Trop de tentatives. Le fallback photo ou une intervention admin peut être nécessaire.',
      };
    }
    if (confirmation.photo_required) {
      return {
        key: 'delivery-confirmation-photo',
        label: 'Photo requise',
        tone: 'neutral',
        description: confirmation.fallback_reason || 'La remise sera confirmée avec une preuve photo.',
      };
    }
    return null;
  };

  const resetPodForm = () => {
    setPodOtpCode('');
    setPodComment('');
    setPodProofType('delivered');
    setPodPhotoUrl('');
    setPodPhotoFileId('');
    setPodPhotoFileName('');
  };

  const openPodDialog = (delivery) => {
    setSelectedDelivery(delivery);
    resetPodForm();
    setShowPodDialog(true);
  };

  const handleGenerateDeliveryOtp = async () => {
    if (!selectedDelivery?.id) return;

    setPodBusy(true);
    try {
      const response = await axios.post(`${API}/deliveries/${selectedDelivery.id}/generate-otp`);
      if (response.data?.delivery_confirmation) {
        syncDeliveryState(selectedDelivery.id, {
          delivery_confirmation: response.data.delivery_confirmation,
        });
      }
      toast.success(response.data?.message || 'OTP généré');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la génération de l'OTP");
    } finally {
      setPodBusy(false);
    }
  };

  const handleValidateDeliveryOtp = async () => {
    if (!selectedDelivery?.id) return;
    const normalizedCode = String(podOtpCode || '').trim();
    if (!normalizedCode) {
      toast.error('Veuillez saisir le code OTP');
      return;
    }

    setPodBusy(true);
    try {
      const response = await axios.post(
        `${API}/deliveries/${selectedDelivery.id}/validate-otp`,
        null,
        { params: { code: normalizedCode } }
      );
      const nextConfirmation = response.data?.delivery_confirmation;
      if (nextConfirmation) {
        syncDeliveryState(selectedDelivery.id, {
          delivery_confirmation: nextConfirmation,
          status: response.data?.delivery_status || selectedDelivery.status,
        });
      }
      toast.success(response.data?.message || 'OTP validé');
      if (nextConfirmation?.effective_mode === 'otp_and_photo' && nextConfirmation?.otp_verified) {
        setPodOtpCode('');
      } else {
        setShowPodDialog(false);
        resetPodForm();
        await loadDeliveries();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de validation OTP');
    } finally {
      setPodBusy(false);
    }
  };

  const handleUploadProof = async () => {
    if (!selectedDelivery?.id) return;
    const confirmation = getDeliveryConfirmation(selectedDelivery);
    if (podProofType === 'delivered') {
      if (confirmation.effective_mode === 'otp_only' && !confirmation.fallback_to_photo_available) {
        toast.error('OTP requis avant validation finale de cette livraison.');
        return;
      }
      if (confirmation.effective_mode === 'otp_and_photo' && !confirmation.otp_verified) {
        toast.error("Validez d'abord l'OTP client avant d'envoyer la photo de remise.");
        return;
      }
    }
    if (!podPhotoUrl && !podPhotoFileId) {
      toast.error('Ajoutez une photo preuve');
      return;
    }

    setPodBusy(true);
    try {
      const response = await axios.post(
        `${API}/deliveries/${selectedDelivery.id}/upload-proof`,
        null,
        {
          params: {
            photo_url: podPhotoUrl,
            photo_file_id: podPhotoFileId || undefined,
            proof_type: podProofType,
            comment: podComment || undefined
          }
        }
      );
      toast.success(response.data?.success ? 'Preuve photo enregistrée' : 'Preuve envoyée');
      setShowPodDialog(false);
      resetPodForm();
      loadDeliveries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi de la preuve");
    } finally {
      setPodBusy(false);
    }
  };

  const handleReportClientAbsent = async () => {
    if (!selectedDelivery?.id) return;

    setPodBusy(true);
    try {
      const response = await axios.post(
        `${API}/deliveries/${selectedDelivery.id}/report-client-absent`,
        null,
        {
          params: {
            comment: podComment || undefined
          }
        }
      );
      toast.success(response.data?.message || 'Absence client signalée');
      setShowPodDialog(false);
      resetPodForm();
      loadDeliveries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du signalement');
    } finally {
      setPodBusy(false);
    }
  };

  const handleProofFileSelection = (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedDelivery?.id) return;
    if (!POD_PHOTO_ALLOWED_TYPES.has(file.type)) {
      toast.error('Format image non supporté. Utilisez JPG, PNG ou WEBP.');
      event.target.value = '';
      return;
    }
    if (file.size > POD_PHOTO_MAX_SIZE_BYTES) {
      toast.error('Photo trop lourde. Taille maximum : 8 Mo.');
      event.target.value = '';
      return;
    }

    const uploadProofFile = async () => {
      const formData = new FormData();
      formData.append('file', file);
      setPodBusy(true);
      try {
        const response = await axios.post(
          `${API}/deliveries/${selectedDelivery.id}/upload-proof-file`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        const uploadedFile = response.data?.data?.file;
        if (!uploadedFile?.id) {
          throw new Error('missing_file_id');
        }
        setPodPhotoFileId(uploadedFile.id);
        setPodPhotoFileName(uploadedFile.original_filename || file.name);
        setPodPhotoUrl('');
        toast.success('Photo preuve téléversée de manière sécurisée');
      } catch (error) {
        console.warn('Impossible de téléverser la preuve photo.');
        toast.error(error.response?.data?.detail || 'Erreur lors du téléversement de la photo');
        event.target.value = '';
      } finally {
        setPodBusy(false);
      }
    };

    uploadProofFile();
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'paid': { label: 'Payé', class: 'bg-blue-100 text-blue-700' },
      'pickup_in_progress': { label: 'En route vers vendeur', class: 'bg-amber-100 text-amber-700' },
      'picked_up': { label: 'Colis récupéré', class: 'bg-purple-100 text-purple-700' },
      'in_transit': { label: 'En transit', class: 'bg-cyan-100 text-cyan-700' },
      'at_relay': { label: 'Au Point de Relais', class: 'bg-indigo-100 text-indigo-700' },
      'out_for_delivery': { label: 'En livraison', class: 'bg-green-100 text-green-700' },
      'delivered_with_proof': { label: 'Livré avec preuve', class: 'bg-emerald-100 text-emerald-700' },
      'client_absent': { label: 'Client absent', class: 'bg-amber-100 text-amber-700' },
      'delivery_rescheduled': { label: 'Reportée', class: 'bg-indigo-100 text-indigo-700' },
      'delivery_failed': { label: 'Échec', class: 'bg-red-100 text-red-700' }
    };
    const info = statusMap[status] || { label: status, class: 'bg-gray-100 text-gray-700' };
    return <Badge className={info.class}>{info.label}</Badge>;
  };

  const getOperationalBadgeClass = (tone) => {
    const styles = {
      critical: 'bg-red-100 text-red-700 border border-red-200',
      express: 'bg-gradient-to-r from-amber-400 to-orange-500 text-white',
      premium: 'bg-violet-100 text-violet-700 border border-violet-200',
      trust: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
      neutral: 'bg-slate-100 text-slate-700 border border-slate-200',
      protection: 'bg-blue-100 text-blue-700 border border-blue-200',
    };
    return styles[tone] || styles.neutral;
  };

  const prioritizedDeliveries = deliveries.filter((delivery) => Boolean(delivery.is_priority));
  const otherDeliveries = deliveries.filter((delivery) => !delivery.is_priority);
  const selectedDeliveryConfirmation = getDeliveryConfirmation(selectedDelivery);
  const selectedDeliveryConfirmationBadge = getDeliveryConfirmationBadge(selectedDelivery);
  const selectedOtpStatus = String(selectedDeliveryConfirmation.otp.status || '').trim().toLowerCase();
  const selectedOtpStatusLabel = {
    pending: 'OTP en attente',
    verified: 'OTP validé',
    expired: 'OTP expiré',
    locked: 'OTP bloqué',
    cancelled: 'OTP annulé',
    used: 'OTP déjà utilisé',
    not_generated: 'OTP non généré',
    not_available: 'OTP indisponible',
  }[selectedOtpStatus] || 'OTP non généré';
  const selectedOtpActionLabel = selectedOtpStatus === 'pending' ? 'Renvoyer OTP' : 'Générer OTP';

  const renderDeliveryCard = (delivery) => {
    const deliveryBadges = Array.isArray(delivery.priority_badges) ? [...delivery.priority_badges] : [];
    const confirmationBadge = getDeliveryConfirmationBadge(delivery);
    if (confirmationBadge) {
      deliveryBadges.push({
        key: confirmationBadge.key,
        label: confirmationBadge.label,
        tone: confirmationBadge.tone,
      });
    }

    return (
      <Card
        key={delivery.id}
        data-delivery-card-id={delivery.id}
        className={`card-hover ${
          focusedDeliveryId === delivery.id ? 'ring-2 ring-cyan-400 ring-offset-2' : ''
        } ${
          delivery.is_priority ? 'border-red-200 bg-red-50/30' : ''
        }`}
      >
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <CardTitle className="text-lg">Livraison #{delivery.id.slice(0, 8)}</CardTitle>
              </div>
              <CardDescription>Client : {delivery.client_info.full_name}</CardDescription>
              {deliveryBadges.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid={`delivery-badges-${delivery.id}`}>
                  {deliveryBadges.map((badge) => (
                    <Badge
                      key={`${delivery.id}-${badge.key}`}
                      className={getOperationalBadgeClass(badge.tone)}
                    >
                      {badge.key === 'priority' && <AlertTriangle className="w-3 h-3 mr-1" />}
                      {badge.label}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            {getStatusBadge(delivery.status)}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div
              className={`rounded-lg border px-3 py-2 ${
                delivery.is_priority
                  ? 'border-red-200 bg-red-50 text-red-700'
                  : 'border-slate-200 bg-slate-50 text-slate-700'
              }`}
              data-testid={`priority-summary-${delivery.id}`}
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide">
                Groupe {delivery.priority_group} · Score {delivery.priority_score}
              </p>
              <p className="mt-1 text-sm font-medium">{delivery.priority_label}</p>
              <p className="mt-1 text-xs opacity-80">{delivery.priority_reason}</p>
            </div>

            {confirmationBadge && (
              <div
                className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700"
                data-testid={`delivery-confirmation-summary-${delivery.id}`}
              >
                <p className="text-[11px] font-semibold uppercase tracking-wide">
                  Validation de remise
                </p>
                <p className="mt-1 text-sm font-medium">{confirmationBadge.label}</p>
                <p className="mt-1 text-xs opacity-80">{confirmationBadge.description}</p>
              </div>
            )}

            {delivery.delivery_proof_photo_url && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                <p className="font-medium">Preuve photo enregistrée</p>
                <a
                  href={delivery.delivery_proof_photo_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-medium underline"
                >
                  Voir la photo de preuve
                </a>
                <p className="mt-1 text-xs">Conservation : 7 jours après livraison terminée.</p>
              </div>
            )}

            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-red-600 mt-1" />
              <div className="text-sm">
                <p className="font-medium">Ramassage</p>
                <p className="text-gray-600">{delivery.pickup_address}, {delivery.pickup_district}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-green-600 mt-1" />
              <div className="text-sm">
                <p className="font-medium">Livraison</p>
                <p className="text-gray-600">{delivery.delivery_address}, {delivery.delivery_district}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 pt-2 border-t">
              <div className="text-sm">
                <p className="text-gray-500">Poids</p>
                <p className="font-medium">{delivery.weight} kg</p>
              </div>
              <div className="text-sm">
                <p className="text-gray-500">Distance</p>
                <p className="font-medium">{delivery.distance_km.toFixed(1)} km</p>
              </div>
            </div>

            <Button
              data-testid={`update-status-btn-${delivery.id}`}
              className="w-full mt-4 bg-red-500 hover:bg-red-600"
              onClick={() => {
                setSelectedDelivery(delivery);
                setNewStatus('');
                setWeightVerified('');
                setShowStatusDialog(true);
              }}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {UI_ACTION_LABELS.updateStatus}
            </Button>
            {isPodEligibleDelivery(delivery) && (
              <Button
                variant="outline"
                className="w-full mt-2"
                onClick={() => openPodDialog(delivery)}
              >
                <ShieldCheck className="w-4 h-4 mr-2" />
                Valider livraison (POD)
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderDeliverySection = ({ title, description, items, emptyMessage, sectionTestId }) => (
    <section className="space-y-4" data-testid={sectionTestId}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
        <Badge className="bg-slate-100 text-slate-700 border border-slate-200">
          {items.length}
        </Badge>
      </div>

      {items.length === 0 ? (
        <Card className="border-dashed border-slate-200">
          <CardContent className="py-8 text-center text-sm text-slate-500">
            {emptyMessage}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {items.map((delivery) => renderDeliveryCard(delivery))}
        </div>
      )}
    </section>
  );

  const handleLogout = () => {
    clearAuthSession();
    navigate('/');
    toast.success(UI_FEEDBACK_MESSAGES.seeYouSoon);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 h-screen w-64 shrink-0 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="h-full flex flex-col">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={logoSidebar} alt="Coli Noki" className="h-12 w-28 object-contain object-left" />
                <div>
                  <p className="text-xs text-gray-500">{UI_ROLE_LABELS.courier}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <div className="space-y-2">
              <Button
                variant={activeTab === 'missions' ? 'default' : 'ghost'}
                className="w-full justify-start"
                onClick={() => setActiveTab('missions')}
              >
                <Package className="w-4 h-4 mr-2" />
                {UI_NAV_LABELS.missions}
              </Button>
              <Button
                variant={activeTab === 'tracking' ? 'default' : 'ghost'}
                className="w-full justify-start"
                onClick={() => setActiveTab('tracking')}
              >
                <Search className="w-4 h-4 mr-2" />
                {UI_NAV_LABELS.tracking}
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start"
                onClick={() => navigate('/account/settings')}
              >
                <Settings className="w-4 h-4 mr-2" />
                {UI_NAV_LABELS.settings}
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start"
                onClick={() => navigate('/account/notifications')}
              >
                <Bell className="w-4 h-4 mr-2" />
                {UI_NAV_LABELS.notificationPreferences}
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start"
                onClick={() => navigate('/account/addresses')}
              >
                <MapPin className="w-4 h-4 mr-2" />
                {UI_NAV_LABELS.savedAddresses}
              </Button>
            </div>

            <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200 mt-4">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-green-600 mb-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-sm font-medium">Position active</span>
                </div>
                <p className="text-xs text-gray-600">Votre position est partagée avec l'admin</p>
              </CardContent>
            </Card>
          </div>

          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.full_name || 'Livreur'}</p>
                <p className="text-xs text-gray-500">{user?.city || 'Ville non renseignée'}</p>
              </div>
            </div>
            <Button
              data-testid="logout-btn"
              variant="outline"
              className="w-full"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              {UI_ACTION_LABELS.logout}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </Button>
            <h2 className="text-2xl font-bold">
              {activeTab === 'missions' ? UI_NAV_LABELS.missions : UI_NAV_LABELS.tracking}
            </h2>
            <NotificationBell />
          </div>
        </header>

        <main className="flex-1 p-6 overflow-y-auto">
          {activeTab === 'tracking' ? (
            <TrackingSearch />
          ) : (
            <>
              {deliveries.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Aucune livraison assignée pour le moment</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-8">
                  <Card className="border-slate-200 bg-white/90">
                    <CardContent className="p-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Organisation des missions</p>
                        <p className="text-sm text-slate-500">
                          {prioritizedDeliveries.length} prioritaire(s) sur {deliveries.length} mission(s) attribuee(s).
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant={priorityOnly ? 'default' : 'outline'}
                        data-testid="priority-only-toggle"
                        onClick={() => setPriorityOnly((currentValue) => !currentValue)}
                      >
                        {priorityOnly ? 'Afficher toutes les missions' : 'Prioritaires uniquement'}
                      </Button>
                    </CardContent>
                  </Card>

                  {renderDeliverySection({
                    title: 'Livraisons prioritaires',
                    description: 'Missions a traiter en premier selon le type de service et le niveau vendeur.',
                    items: prioritizedDeliveries,
                    emptyMessage: priorityOnly
                      ? 'Aucune livraison prioritaire pour le moment.'
                      : 'Aucune mission prioritaire n est disponible actuellement.',
                    sectionTestId: 'priority-deliveries-section',
                  })}

                  {!priorityOnly && renderDeliverySection({
                    title: 'Autres livraisons',
                    description: 'Missions restantes, deja triees par priorite puis anciennete.',
                    items: otherDeliveries,
                    emptyMessage: 'Aucune autre mission disponible.',
                    sectionTestId: 'other-deliveries-section',
                  })}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Status Update Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mettre à jour le statut</DialogTitle>
            <DialogDescription>
              {selectedDelivery ? `Livraison #${selectedDelivery.id.slice(0, 8)}` : 'Sélectionnez une livraison'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nouveau statut</Label>
              <Select value={newStatus} onValueChange={setNewStatus} disabled={statusUpdating}>
                <SelectTrigger data-testid="status-select">
                  <SelectValue placeholder="Sélectionnez un statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pickup_in_progress">En route vers vendeur</SelectItem>
                  <SelectItem value="picked_up">Colis récupéré</SelectItem>
                  <SelectItem value="in_transit">En transit</SelectItem>
                  <SelectItem value="at_relay">Arrivé au Point de Relais</SelectItem>
                  <SelectItem value="out_for_delivery">En route vers client</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newStatus === 'picked_up' && (
              <div>
                <Label>Poids réel (kg)</Label>
                <Input
                  type="number"
                  step="0.1"
                  data-testid="weight-verified-input"
                  placeholder={`Annoncé : ${selectedDelivery?.weight ?? '-'} kg`}
                  value={weightVerified}
                  onChange={(e) => setWeightVerified(e.target.value)}
                  disabled={statusUpdating}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Si le poids diffère, une alerte sera envoyée
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)} disabled={statusUpdating}>
              {UI_ACTION_LABELS.cancel}
            </Button>
            <Button
              data-testid="confirm-status-btn"
              className="bg-red-500 hover:bg-red-600"
              onClick={handleUpdateStatus}
              disabled={!newStatus || statusUpdating}
            >
              {statusUpdating ? 'Mise à jour...' : UI_ACTION_LABELS.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPodDialog} onOpenChange={setShowPodDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Validation de livraison (POD)</DialogTitle>
            <DialogDescription>
              {selectedDelivery
                ? `Livraison #${selectedDelivery.id.slice(0, 8)} - OTP client, preuve photo et cas de remise terrain.`
                : 'Sélectionnez une livraison.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="pod-confirmation-summary">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-600" />
                <div className="space-y-2">
                  <div>
                    <p className="font-medium text-slate-900">
                      {selectedDeliveryConfirmationBadge?.label || 'Validation de remise'}
                    </p>
                    <p className="text-sm text-slate-600">
                      {selectedDeliveryConfirmationBadge?.description || 'Le backend détermine si la remise se fait par photo, OTP ou les deux.'}
                    </p>
                  </div>
                  {selectedDeliveryConfirmation.tracking_email_confirmed && selectedDeliveryConfirmation.tracking_email_masked && !selectedDeliveryConfirmation.otp_verified ? (
                    <p className="text-xs text-slate-500">
                      OTP envoyé à {selectedDeliveryConfirmation.tracking_email_masked}
                    </p>
                  ) : null}
                  {selectedDeliveryConfirmation.fallback_reason ? (
                    <p className="text-xs text-amber-700">{selectedDeliveryConfirmation.fallback_reason}</p>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="font-medium">Validation OTP</p>
                  <p className="text-xs text-gray-500">
                    {selectedDeliveryConfirmation.otp_required
                      ? 'Générez puis validez le code reçu par le client.'
                      : 'OTP indisponible pour cette remise. Le flux photo reste disponible.'}
                  </p>
                </div>
                <Badge className={getOperationalBadgeClass(
                  selectedOtpStatus === 'pending'
                    ? 'protection'
                    : selectedOtpStatus === 'verified'
                      ? 'trust'
                      : selectedOtpStatus === 'expired' || selectedOtpStatus === 'locked'
                        ? 'critical'
                        : 'neutral'
                )}>
                  {selectedOtpStatusLabel}
                </Badge>
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-gray-500">
                  {selectedDeliveryConfirmation.otp_verified
                    ? 'Le code client a déjà été validé.'
                    : selectedDeliveryConfirmation.otp_required
                      ? 'Le livreur doit saisir le code communiqué par le client avant la remise finale.'
                      : 'Aucun email de suivi confirmé n est disponible, donc l OTP n est pas utilisable.'}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGenerateDeliveryOtp}
                  disabled={podBusy || !selectedDelivery || !selectedDeliveryConfirmation.otp_required || selectedDeliveryConfirmation.otp_verified}
                >
                  {selectedOtpActionLabel}
                </Button>
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Saisir OTP client"
                  value={podOtpCode}
                  onChange={(event) => setPodOtpCode(event.target.value)}
                  disabled={!selectedDeliveryConfirmation.otp_required || selectedDeliveryConfirmation.otp_verified}
                />
                <Button
                  type="button"
                  onClick={handleValidateDeliveryOtp}
                  disabled={podBusy || !podOtpCode.trim() || !selectedDeliveryConfirmation.otp_required || selectedDeliveryConfirmation.otp_verified}
                >
                  Valider OTP
                </Button>
              </div>
            </div>

            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Camera className="w-4 h-4 text-blue-600" />
                <p className="font-medium">Preuve photo</p>
              </div>
              <p className="text-xs text-gray-500">
                {selectedDeliveryConfirmation.effective_mode === 'otp_and_photo'
                  ? (selectedDeliveryConfirmation.otp_verified
                    ? 'OTP validé. Ajoutez maintenant la photo de remise pour finaliser.'
                    : 'L OTP doit être validé avant l envoi de la photo de remise.')
                  : selectedDeliveryConfirmation.effective_mode === 'otp_only'
                    ? (selectedDeliveryConfirmation.fallback_to_photo_available
                      ? 'Le mode photo de secours est disponible pour cette livraison.'
                      : 'La photo seule ne finalise pas cette livraison tant que l OTP reste requis.')
                    : 'La preuve photo reste le mode de validation principal pour cette remise.'}
              </p>
              <div>
                <Label>Type de preuve</Label>
                <Select value={podProofType} onValueChange={setPodProofType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="delivered">Livré avec photo</SelectItem>
                    <SelectItem value="client_absent">Client absent (photo)</SelectItem>
                    <SelectItem value="delivery_rescheduled">Livraison reportée</SelectItem>
                    <SelectItem value="delivery_failed">Livraison refusée / échouée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Photo (fichier)</Label>
                <Input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={handleProofFileSelection} disabled={podBusy || !selectedDelivery} />
                {podPhotoFileName ? (
                  <p className="text-xs text-emerald-700">Fichier sécurisé prêt : {podPhotoFileName}</p>
                ) : (
                  <p className="text-xs text-gray-500">Prendre une photo ou importer une image JPG, PNG ou WEBP (8 Mo max). Les preuves sont conservées 7 jours après livraison terminée.</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label>Commentaire</Label>
                <Textarea
                  placeholder="Commentaire facultatif"
                  value={podComment}
                  onChange={(event) => setPodComment(event.target.value)}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleUploadProof}
                disabled={podBusy || !selectedDelivery}
              >
                Envoyer preuve photo
              </Button>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="w-4 h-4" />
                <p className="font-medium">Client absent</p>
              </div>
              <p className="text-xs text-amber-700">
                Utiliser cette action rapide si le client est absent sans preuve photo.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={handleReportClientAbsent}
                disabled={podBusy || !selectedDelivery}
              >
                Signaler client absent
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPodDialog(false);
                resetPodForm();
              }}
            >
              {UI_ACTION_LABELS.close}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default LivreurDashboard;

